// @ts-check
'use_strict';
import { ToggleContent } from '../tv_utils/toggle_content';
import { FlexGridBoxes } from '../tv_utils/flex_grid_boxes';
import { IAddress } from './IAddress';
export declare var $;

/**
 * UI class to handle interactions pertaining to shipping estimations.
 */

export class ShippingEstimator {
    /**
     * @var {ToggleContent} carrier_tabs controls to display the carrier and their shipping options.
     * @var {number} carrier_tab_index tracks the active carrier to hold position between updates.
     * @var {Object} chosen_record the current shipping option that is selected.
     * @var {{ [key: string]: any }} elements relevant on the DOM.
     * @var {FlexGridBoxes} flex_grid the shipping options displayed in the tab content.
     * @var {Function} select_option_callback to update the caller when shipping is selected.
     * @var {boolean} product_page whether or not we're on the product page.
     * @var {boolean} fetching wether or not we're fetching the shipping estimation already.
     */
    carrier_tabs: ToggleContent;
    carrier_tab_index: number;
    chosen_record: Object;
    elements: { [key: string]: any; };
    flex_grid: FlexGridBoxes;
    select_option_callback: Function;
    product_page: boolean;
    body_rendered_callback: Function;
    fetching: boolean;
    order_cost: number;
    tax_rate: number;

    /**
     * Instantiate the class, bind to the dom, and attach events.
     *
     * @param {Function} select_option_callback to call when an option is selected.
     * @param {boolean} product_page tells the estimator if this is a product page.
     * @param {Function} body_rendered_callback for after the body is rendered.
     * @returns {ShippingEstimator} instantiated and bound.
     */
    constructor(
        select_option_callback: Function = null,
        product_page: boolean = false,
        body_rendered_callback: Function = null
    ) {
        this.elements = {};
        this.carrier_tab_index = 0;
        this.select_option_callback = select_option_callback;
        this.product_page = product_page;
        this.body_rendered_callback = body_rendered_callback;
        this.fetching = false;
        this.order_cost = 0;
        this.tax_rate = 0;

        this.bind();
        this.bind_boxing_data();
    }

    /**
     * Gets the carrier of the active tab.
     *
     * @returns {string} carrier name.
     */
    active_carrier(): string {
        return this.carrier_tabs.content[this.carrier_tabs.active_idx].id.replace('tab_', '');
    }

    /**
     * Create references to the relevant DOM elements.
     *
     * @returns {void}
     */
    bind(): void {
        this.elements.results_container = document.getElementById('shipping-estimate-results-container');
        this.elements.selected_shipping = document.getElementById('js-selected-shipping');
        this.elements.selected_shipping_option = document.getElementById('js-selected-shipping-option');
    }

    /**
     * Rebinds the boxing data when the shipping estimator is opened.
     *
     * @returns {void}
     */
    bind_boxing_data() {
        this.elements.boxing_data = <HTMLInputElement>document.getElementById('js-shipping-estimate-boxing-data-input');
    }

    /**
     * Calls out to the back-end to get the updated in store pickup date based on production data.
     *
     * @param {string} production_code to base the calculation on.
     * @return {void}
     */
    calculate_in_store_pickup_date(production_code: string): void {
        const url = 'index.php?dispatch=tv_shipping.calculate_in_store_pickup_date';

        $.ceAjax('request', url, {
            method: 'POST',
            cache: false,
            data: {
                production_code: production_code
            },
            result_ids: 'js-summary-in-store-pickup-option-section',
            callback: (response: object) => {
                const pickup_date = response['json']['pickup_date'];
                const pickup_date_summary = document.getElementById('js-pickup-selected-time');
                if (pickup_date_summary) {
                    pickup_date_summary.innerHTML = pickup_date;
                }
            }
        });
    }

    /**
     * Fetches shipping estimates for the given boxing data and address information.
     *
     * @param {IAddress} address to fetch by.
     * @param {string} production_code to base the dates on.
     * @param {string} carrier to fetch information for.
     * @param {boolean} is_product_or_cart to check if estimated from product or cart page.
     * @returns {Promise<{}>} cs cart ajax response and callback.
     */
    async fetch_estimates(
        address: IAddress,
        production_code: string,
        carrier?: string,
        is_product_or_cart: boolean = true
    ): Promise<void> {
        if (this.fetching) {
            return;
        }

        this.fetching = true;
        if (!address.zipcode) {
            return new Promise(resolve => {
                this.fetching = false;
                resolve();
            });
        }

        if (this.elements.selected_shipping_option) {
            this.elements.selected_shipping_option.innerHTML = '';
        }

        return new Promise((resolve, reject) => {
            // Validate address to determine if it's residential or commercial.
            $.ceAjax('request', 'index.php?dispatch=tv_shipping.validate_address', {
                method: 'POST',
                message: 'Getting shipping estimates',
                data: {
                    address: address
                },
                callback: response => {
                    address.residential = response.json.residential;
                    $.ceAjax('request', 'index.php?dispatch=tv_shipping.estimate_results', {
                        method: 'POST',
                        message: 'Getting shipping estimates',
                        data: {
                            boxing_data: this.elements.boxing_data.value,
                            address: address,
                            production: production_code,
                            order_cost: this.order_cost,
                            carrier: carrier
                        },
                        cache: false,
                        result_ids: 'shipping-estimate-results-container',
                        callback: response => {
                            this.fetching = false;
                            this.fetch_estimates_html_callback(is_product_or_cart);
                            resolve(response['results']);

                            // Keep flex boxes on cart and product page hidden.
                            if (document.getElementById('js-shipping-zipcode-button')) {
                                document.getElementById('shipping-estimate-results').classList.add('tv-display__none');
                            }
                        },
                        error_callback: err => {
                            this.fetching = false;
                            this.fetch_estimates_html_callback(is_product_or_cart);
                            reject();
                        }
                    });
                }
            });
        });
    }

    /**
     * Render and re-bind the results of a shipping estimation.
     *
     * @return {void}
     */
    fetch_estimates_html_callback(is_product_or_cart): void {
        // This is after the results container gets filled in.
        this.elements.shipping_results = document.getElementById('shipping-estimate-results');
        if (document.getElementsByClassName('js-shipping-tab').length <= 0) {
            this.use_estimate(null);
            return;
        }

        //  Rebind the new shipping options into tabs and the flex grid.
        const content = document.getElementsByClassName('js-shipping-tab-content') as HTMLCollectionOf<HTMLElement>;
        const buttons = {
            links: document.getElementsByClassName('js-shipping-tab'),
            mobile_links: document.getElementsByClassName('js-estimate-results-mobile-tablink')
        };

        this.carrier_tabs = new ToggleContent(
            content,
            buttons,
            0,
            false
        );

        const shipping_rate_options = document.getElementsByClassName('js-shipping-rate-choose-button');

        this.flex_grid = new FlexGridBoxes(
            shipping_rate_options as HTMLCollectionOf<HTMLElement>,
            {},
            false,
            null,
            this.use_estimate.bind(this)
        );

        this.flex_grid.setup();
        if (is_product_or_cart) {
            if ((this.elements.see_more_btn &&
                this.elements.see_more_btn.classList.contains('selected')
            ) || (
                    document.getElementById('js-checkout-shipping-see-more') &&
                    document.getElementById('js-checkout-shipping-see-more').classList.contains('selected'))) {
                this.elements.shipping_results.style.display = 'block';
            }

            this.body_rendered_callback = null;
        }
        this.flex_grid.select_by_idx(0);

        if (this.body_rendered_callback) {
            this.body_rendered_callback();
        }
    }

    /**
     * Show or hide the prices in the results.
     *
     * @param {boolean} hide true to hide.
     * @return {void}
     */
    toggle_prices(hide: boolean): void {
        let display = 'inline';
        if (hide) {
            display = 'none';
        }

        const option_prices = <HTMLCollectionOf<HTMLSpanElement>>document.getElementsByClassName(
            'js-shipping-option-price'
        );
        for (const price of option_prices) {
            price.style.display = display;
        }
    }

    /**
     * Selects a shipping price result to be used as the shipping method/price.
     *
     * @param {HTMLElement} elem The element that was clicked to select the shipment service/amount.
     * @returns {void}
     */
    use_estimate(elem: HTMLElement): void {
        if (!elem) {
            // ESTERROR
            this.chosen_record = {
                amount: 0,
                carrier: '',
                cartonId: 0,
                code: 'ESTERROR',
                estimatedDeliveryDate: 'N/A',
                estimatedDeliveryTime: 'N/A',
                name: 'ESTERROR',
                quote_calc_time: new Date(),
                quote_expiry_time: 'N/A',
                transitDays: 0
            };
        } else {
            // Store it locally.
            this.chosen_record = JSON.parse(elem.dataset.rec);
        }

        if (this.select_option_callback) {
            // Update the caller with the option that was chosen.
            this.select_option_callback(elem);
        }

        // This logic is only needed in the cart, so we're done.
        if (this.product_page) {
            // Fill out the summary selected shipping element.
            let selected_shipping_option;
            if (this.flex_grid) {
                selected_shipping_option = this.flex_grid.selected_element();
            }

            // Selected shipping option will be null when shipping api returns error. So check for null.
            if (selected_shipping_option != null) {

                // Copy the selected element to a hidden text, just for product and cart pages.
                const selected_option_product_page = selected_shipping_option.dataset.rec;
                if (document.getElementById('js-selected-shipping-product')) {
                    (<HTMLInputElement>document.getElementById('js-selected-shipping-product')).value =
                        selected_option_product_page;
                }
            }

            return;
        }

        // Update the form input so that it posts the selection.
        if (this.elements.selected_shipping) {
            this.elements.selected_shipping.value = JSON.stringify(this.chosen_record);
        }
    }
}
